import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText
} from '@material-ui/core';
import { IList, mapNullable, stringToNumber } from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { floorOptions } from '../../../constants/floor-options';
import { viewTexts } from '../../../constants/view-texts';
import { ListWithPromiseState } from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import { IModelType } from '../../../types/record/model';
import { MuiButton, MuiInput, MuiSelect } from '../../shared/form-element';

const vt = viewTexts.dialogs.projectImageSettings;

const Body = styled.div`
  display: flex;
`;

const Img = styled.img`
  object-fit: contain;
  max-width: 60%;
  max-height: 60%;
`;

const FormWrapper = styled.div`
  margin-left: 20px;
  width: 100%;
`;

const FormElementWrapper = styled.div`
  padding: 5px;
  max-width: auto;
`;

const InputWrapper = styled(FormElementWrapper)`
  display: flex;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  margin-left: 10px;
`;

interface Props {
  mode: 'init' | 'edit';
  open: boolean;
  onCancelClick: () => void;
  onOkClick: () => void;
  imgSrc: string;
  availableModels: ListWithPromiseState<IModelType>;
  modelId: number | undefined;
  setModelId: (v: number | undefined) => void;
  modelIdErrors: string[] | undefined;
  floorId: string | undefined;
  setFloorId: (v: string | undefined) => void;
  imageName: string | undefined;
  imageNameOnInput: (v: string) => void;
  imageNameErrors: string[] | undefined;
  autoCompleteButtonIsDisabled: boolean;
  onAutoCompleteClick: () => void;
  submitButtonEnabled: boolean;
  nonFieldErrors: string[];
}

export const ProjectImageSettingsDialogView: FC<Props> = memo(
  ({
    mode,
    open,
    onCancelClick,
    onOkClick,
    imgSrc,
    availableModels,
    modelId,
    setModelId,
    floorId,
    setFloorId,
    modelIdErrors = [],
    imageName = '',
    imageNameOnInput,
    imageNameErrors = [],
    autoCompleteButtonIsDisabled,
    onAutoCompleteClick,
    submitButtonEnabled,
    nonFieldErrors
  }: Readonly<Props>) => {
    const onSelect = useCallback(
      (id: string | undefined) => {
        setModelId(mapNullable(id, stringToNumber));
      },
      [setModelId]
    );

    const options = useMemo(
      () =>
        mapListLatest(availableModels, (list: IList<IModelType>): {
          id: string;
          label: string;
        }[] =>
          list
            .map(model => ({
              id: model.id.toString(),
              label: model.name
            }))
            .toJS()
        ),
      [availableModels]
    );

    const modelIdStr = useMemo<string | undefined>(
      () => mapNullable(modelId, e => e.toString()),
      [modelId]
    );

    return (
      <Dialog disableBackdropClick={true} open={open} maxWidth={'xl'}>
        <DialogTitle>{vt.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{vt.subTitle}</DialogContentText>
          <Body>
            <Img src={imgSrc} alt='' />
            <FormWrapper>
              <FormElementWrapper>
                <MuiSelect
                  label={vt.label1}
                  required={true}
                  selectedId={modelIdStr}
                  options={options.value.latest}
                  onChange={onSelect}
                  helperText={modelIdErrors.join(' ')}
                  error={modelIdErrors.length > 0}
                  useEmptyLabel={true}
                />
              </FormElementWrapper>
              <FormElementWrapper>
                <MuiSelect
                  label={vt.label2}
                  required={false}
                  selectedId={floorId}
                  options={floorOptions}
                  onChange={setFloorId}
                  useEmptyLabel={true}
                />
              </FormElementWrapper>
              <InputWrapper>
                <MuiInput
                  label={vt.label3}
                  required={true}
                  value={imageName}
                  onInput={imageNameOnInput}
                  helperText={imageNameErrors.join(' ')}
                  error={imageNameErrors.length > 0}
                />
                <ButtonWrapper>
                  <MuiButton
                    label={vt.autoCompleteButton}
                    onClick={onAutoCompleteClick}
                    disabled={autoCompleteButtonIsDisabled}
                  />
                </ButtonWrapper>
              </InputWrapper>
              {nonFieldErrors.map((er, i) => (
                <FormHelperText key={i} error={true}>
                  {er}
                </FormHelperText>
              ))}
            </FormWrapper>
          </Body>
        </DialogContent>
        <DialogActions>
          {mode === 'edit' ? (
            <Button onClick={onCancelClick} color='default'>
              {vt.cancel}
            </Button>
          ) : (
            undefined
          )}
          <Button
            onClick={onOkClick}
            color='primary'
            disabled={!submitButtonEnabled}
          >
            {vt.ok}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

ProjectImageSettingsDialogView.displayName = 'ProjectImageSettingsDialogView';
